import { useMemo, useState } from 'react';
import {
  useGetBankDocumentsQuery,
  useImportBankDocumentCsvMutation,
} from '../Service/bankDocumentsService';
import {
  getBankDocumentsErrorCode,
  getBankDocumentsErrorMessage,
} from '../Utils/bankDocumentsErrorMapping';
import { bankDocumentsLogger } from '../Utils/bankDocumentsLogger';
import { mapBankDocumentsDtoToModel } from '../Mapping/bankDocumentsMapper';
import type { ImportCsvDialogSubmitArgs } from '../Components/ImportCsvDialog';

export const useBankDocumentsPage = () => {
  const { data, isLoading, isError, error } = useGetBankDocumentsQuery();
  const [importBankDocumentCsv, { isLoading: isImportLoading }] =
    useImportBankDocumentCsvMutation();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importSuccessMessage, setImportSuccessMessage] = useState<string | null>(null);
  const [importErrorMessage, setImportErrorMessage] = useState<string | null>(null);

  const documents = useMemo(() => {
    const mapped = mapBankDocumentsDtoToModel(data ?? []);
    return [...mapped].sort(
      (left, right) =>
        new Date(right.importedAtUtc).getTime() - new Date(left.importedAtUtc).getTime()
    );
  }, [data]);

  const errorMessage = getBankDocumentsErrorMessage(
    error,
    'Nie udało się pobrać dokumentów bankowych.'
  );

  const handleOpenImportDialog = () => {
    setImportSuccessMessage(null);
    setImportErrorMessage(null);
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
  };

  const handleImportSubmit = async ({ file, accountNumber }: ImportCsvDialogSubmitArgs) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      bankDocumentsLogger.debug('[BankDocuments] CSV import started', {
        fileName: file.name,
        fileSize: file.size,
        accountNumber,
      });

      const result = await importBankDocumentCsv({ formData, accountNumber }).unwrap();
      const importedRowsCount = result.transactionsCount ?? result.importedRowsCount ?? 0;
      setImportErrorMessage(null);
      setImportSuccessMessage(
        `Import zakończony sukcesem. Dokument ID: ${result.bankDocumentId}, wiersze: ${importedRowsCount}.`
      );
      setImportDialogOpen(false);

      bankDocumentsLogger.debug('[BankDocuments] CSV import success', {
        bankDocumentId: result.bankDocumentId,
        importedRowsCount,
      });
    } catch (uploadError) {
      const mappedMessage = getBankDocumentsErrorMessage(
        uploadError,
        'Nie udało się zaimportować pliku CSV.'
      );
      const errorCode = getBankDocumentsErrorCode(uploadError);

      setImportErrorMessage(mappedMessage);

      bankDocumentsLogger.error('[BankDocuments] CSV import failed', {
        fileName: file.name,
        errorCode,
        mappedMessage,
        uploadError,
      });
    }
  };

  return {
    documents,
    isLoading,
    isError,
    errorMessage,
    importDialogOpen,
    isImportLoading,
    importSuccessMessage,
    importErrorMessage,
    handleOpenImportDialog,
    handleCloseImportDialog,
    handleImportSubmit,
  };
};
